import React from 'react';
import { useDashboardTranslation } from '../i18n';
import { WidgetSkeleton } from './WidgetSkeletons';
import type { SkeletonShape } from '../utils/skeletonShape';
import { useReducedMotion } from '../utils/useReducedMotion';

interface WidgetDataGuardProps {
  loading: boolean;
  error: string | null;
  data: unknown;
  children: React.ReactNode;
  /** Forma del esqueleto de carga. Sin ella cae en `generic`. */
  shape?: SkeletonShape;
  /**
   * Titular del estado vacío, ya traducido por quien lo conoce.
   *
   * <b>Nombra el hueco</b> —"Sin ventas en este periodo", "Ningún ítem por debajo
   * del mínimo"— en vez de "Sin datos disponibles". Dos motivos: un vacío en un
   * tablero suele ser información y no un fallo, y a veces es una buena noticia
   * que merece leerse como tal. El guard no puede saber cuál es el caso, así que
   * lo declara el widget.
   */
  emptyTitle?: string;
  /** Qué puede hacer el usuario al respecto. Ej.: "Cambiar a Mes". */
  emptyHint?: string;
}

/**
 * Guard compartido para widgets: carga, vacío y error.
 *
 * El error se relanza para que lo capture `WidgetErrorBoundary`, que es quien
 * pinta el estado de fallo — así un widget roto no arrastra al resto del tablero.
 */
export const WidgetDataGuard: React.FC<WidgetDataGuardProps> = ({
  loading,
  error,
  data,
  children,
  shape = 'generic',
  emptyTitle,
  emptyHint,
}) => {
  const { t } = useDashboardTranslation();
  const reducedMotion = useReducedMotion();

  if (loading) {
    return <WidgetSkeleton shape={shape} reducedMotion={reducedMotion} />;
  }

  // Lanzar error de fetch antes de verificar data null — evita ErrorBoundary prematuro durante carga inicial
  if (error) {
    throw new Error(error);
  }

  // Un array vacío es tan "sin datos" como un null: hasta ahora sólo se cubría el
  // null, y un endpoint que devolvía [] pintaba un gráfico en blanco sin explicar
  // nada.
  const isEmpty = data == null || (Array.isArray(data) && data.length === 0);

  if (isEmpty) {
    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center text-center px-4 gap-1"
        data-testid="widget-empty"
      >
        <p className="text-[13px]" style={{ color: 'var(--dash-text-secondary)' }}>
          {emptyTitle || t('dashboard.widget.noData')}
        </p>
        {emptyHint && (
          <p className="text-[11px]" style={{ color: 'var(--dash-text-muted)' }}>
            {emptyHint}
          </p>
        )}
      </div>
    );
  }

  return <>{children}</>;
};
